import Card from "components/Card";
import Image from "components/Image";
import Modal from "components/Modal";
import TextEditor from "components/TextEditor";

import { useTranslationContext } from "context/TranslationContext";
import { formatJSTDateFromBE } from "helpers/formatJSTDateFromBE";
import { usePastNotesControllerContext } from "modules/PastNotes/Controller";

import { EPipelineSlug, TGetNotesRes } from "@type/api/management/types";
import { EAccount } from "types/index";

type PastNotesDetailModalProps = {
  selectedNote?: TGetNotesRes["data"][number];
  onCancel?: () => void;
};

const PastNotesDetailModal = ({
  selectedNote,
  onCancel,
}: PastNotesDetailModalProps) => {
  const { t, language } = useTranslationContext();
  const {
    profileImage,
    profileName,
    profileAccountType,
    pipelineSlug,
    onViewProfile,
  } = usePastNotesControllerContext();

  return (
    <Modal
      open={!!selectedNote}
      size="md"
      titleContainerClassName="items-start"
      title={
        <>
          <div className="flex gap-4 mb-4 items-center">
            <Image
              imageUrl={profileImage}
              alt={`staff-${profileName}`}
              label={profileName}
              borderRadius={
                profileAccountType === EAccount.CORPORATION ? "100%" : "20%"
              }
              width={48}
              height={48}
            />
            <div>
              <div className="break-all break-word">{profileName}</div>

              <a
                className="text-blue-500 text-sm cursor-pointer"
                onClick={onViewProfile}
              >
                {t(
                  pipelineSlug == EPipelineSlug.FIELD_SALES
                    ? "view_corporation"
                    : "view_profile"
                )}
              </a>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="">
              <span className="text-gray-800 text-[0.875rem]">
                {t("ticket_id")}:
              </span>{" "}
              {selectedNote?.ticket?.code} - {selectedNote?.ticket?.title}
            </div>
            <div className="w-[1px] h-6 bg-gray-100" />
            <div className="">
              <span className="text-gray-800 text-[0.875rem]">
                {t("date_and_time")}:
              </span>{" "}
              {selectedNote?.createdAt &&
                formatJSTDateFromBE(selectedNote.createdAt, language)}
            </div>
          </div>
        </>
      }
      onCancel={onCancel}
    >
      {selectedNote ? (
        <>
          <Card containerClassName="px-0">
            <div className="font-semibold mb-2">{t("notes")}</div>
            <div className="pt-2">
              <div className="flex items-center justify-between gap-4">
                <div className="text-gray-800 text-sm">
                  {t("recent_conversation_notes")}
                </div>
                <div className="text-gray-800 text-[0.75rem]">
                  {t("last_edited")}:{" "}
                  {formatJSTDateFromBE(selectedNote.createdAt, language)}
                </div>
              </div>
              <TextEditor
                key={selectedNote.id}
                id={`conversation-${selectedNote.id}`}
                value={selectedNote?.recentConversationNotes}
                readOnly
              />
            </div>
            <div className="pt-4">
              <div className="flex items-center justify-between gap-4">
                <div className="text-gray-800 text-sm">{t("notes")}</div>
                <div className="text-gray-800 text-[0.75rem]">
                  {t("last_edited")}:
                  {formatJSTDateFromBE(selectedNote.createdAt, language)}
                </div>
              </div>
              <TextEditor
                key={selectedNote.id}
                id={`notes-${selectedNote.id}`}
                value={selectedNote.notes}
                readOnly
              />
            </div>
            <div className="pt-4">
              <div className="flex items-center justify-between gap-4">
                <div className="text-gray-800 text-sm">{t("tips")}</div>
                <div className="text-gray-800 text-[0.75rem]">
                  {t("last_edited")}:
                  {formatJSTDateFromBE(selectedNote.createdAt, language)}
                </div>
              </div>
              <TextEditor
                key={selectedNote.id}
                id={`tips-${selectedNote.id}`}
                value={selectedNote.tips}
                readOnly
              />
            </div>
          </Card>
        </>
      ) : (
        <p>{t("no_data")}</p>
      )}
    </Modal>
  );
};

export default PastNotesDetailModal;
